<script setup lang="ts">
import {
  PopoverPositionEnum,
  TirPmContextMenuButton,
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPopover,
  TirPmRouterLink,
  TirPmScrollbar,
} from 'tir-components';
import { ChevronRightIcon, UserCircleIcon } from 'tir-style-system/icons/outline';
import { computed, onMounted, ref } from 'vue';
import GetPerformersTaskRequest from '../models/GetPerformersTaskRequestModel';
import { TaskKanbanAdapterService } from '../adapter';
import { TaskModel, TaskPersonModel } from '../models';
import GetPerformersTaskResponse from '../models/GetPerformersTaskResponseModel';
import { AddTaskZmkForUserRequest } from '@/pages/tasks-order-pages/models';
import { RouterLink, useRouter } from 'vue-router';
import GetPerformersTaskItem from '../models/GetPerformersTaskItem';
import { CurrentUser } from 'tir-pm-user-core';

/**Адаптер */
const adapter = new TaskKanbanAdapterService();
/**Роутер */
const router = useRouter();

/**Параметры */
const props = defineProps({
  taskInfo: { type: TaskModel, required: true },
});
/**События */
const emit = defineEmits<{
  /**Закрытие поповера */
  closePopover: [];
}>();

/**Список исполнителей очередности */
const performersListQueue = ref<GetPerformersTaskResponse>();

/**Кол-во исполнителей */
const allUsersInQueue = computed(
  () => performersListQueue.value?.Users?.every((user) => user.InQueue) ?? false,
);

/**После рендера компонента */
onMounted(async () => {
  await getPerformersTask();
});

/**Получить список исполниетелей по задаче(с проверкой на доступ к очереди) */
const getPerformersTask = async () => {
  const resp = await adapter.getPerformersTaskAsync(
    new GetPerformersTaskRequest({
      TaskId: props.taskInfo.Id,
      ShowUsersWithAccessToQueue: true,
    }),
  );
  if (resp.IsSuccess) {
    performersListQueue.value = resp.Value;
  }
};
/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};
/**Добавить в очередность */
const addTaskQueue = async (_userInfo: GetPerformersTaskItem) => {
  if (_userInfo.InQueue) return;
  const resp = await adapter.addTaskZmkForUserAsync(
    new AddTaskZmkForUserRequest({
      TaskId: props.taskInfo.Id,
      UserId: _userInfo.OwnerId,
    }),
  );
  if (resp.IsSuccess) {
    addNotification(`Тикет #${props.taskInfo.Id} добавлен в очередность сотрудника`);
    await getPerformersTask();
    emit('closePopover');
  }
};
/**Маршрут для перехода */
const computedRoute = (_userInfo: GetPerformersTaskItem) => {
  if (_userInfo.InQueue) return {};

  const isCurrentUserInList = CurrentUser.Id === _userInfo.OwnerId;

  return isCurrentUserInList
    ? { name: 'tasks-overview' }
    : { name: 'team-tasks', query: { userId: _userInfo.OwnerId } };
};
</script>
<template>
  <TirPmPopover
    class="task-card__popover-performers"
    isNoTeleport
    :position="PopoverPositionEnum.Right"
  >
    <template #content>
      <TirPmScrollbar showOnHover class="task-card__scrollbar">
        <TirPmRouterLink
          v-for="item in performersListQueue.Users"
          :key="item.OwnerId"
          class="performer-item"
          :isDisabled="item.InQueue"
          :class="{
            'performers-disabled': item.InQueue,
          }"
          :to="computedRoute(item)"
          :target="item.InQueue ? undefined : '_blank'"
          @click="addTaskQueue(item)"
        >
          <TirPmIconView
            :size="24"
            isRounded
            :colorCode="item.AvatarColor"
            :isBordered="false"
            :imageLoadUrl="item.Photo"
          >
            <template #mainSlot>
              {{ item.FirstName[0] + item.LastName[0] }}
            </template>
          </TirPmIconView>
          {{ item.FirstName }} {{ item.LastName }} {{ item.MiddleName }}
        </TirPmRouterLink>
      </TirPmScrollbar>
    </template>
    <TirPmContextMenuButton class="task-card__menu-item-performers">
      <!-- :class="{
        'performers-disabled': allUsersInQueue,
      }" -->
      <div class="task-card__menu-item--add-to-queue-block">
        <!-- :class="{
          'performers-disabled': allUsersInQueue,
        }" -->
        <div class="task-card__menu-item--add-to-queue">
          <UserCircleIcon class="menu-icon" />
          Добавить в очередность сотрудника
        </div>
        <ChevronRightIcon class="menu-icon" />
      </div>
    </TirPmContextMenuButton>
  </TirPmPopover>
</template>

<style scoped lang="scss">
.task-card {
  &__menu-item--add-to-queue {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 0.75rem;
  }
  &__menu-item--add-to-queue-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  &__popover-performers {
    width: 100% !important;
  }
  &__scrollbar {
    max-height: 15rem;
  }
}

.performers-disabled {
  color: var(--text-tertiary, #93969b) !important;
  cursor: default;
  &:hover {
    background: none !important;
  }
}
.menu-icon {
  width: 1.5rem;
  min-width: 1.5rem;
  height: 1.5rem;
}
.performer-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  color: var(--text-primary, #272d37);
  cursor: pointer;
  &:hover {
    background-color: var(--neutral-translucent-5);
  }
}
</style>

<style lang="scss">
.task-card__menu-item-performers .tir-pm-context-menu-button__middle {
  width: 100%;
}
</style>
